import { LayoutElementBasedCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { TitleStyles } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/styles/TitleStyles";
import { useLoggedOutSuppliersPageCurrentCategory } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/Context/useLoggedOutSuppliersPageCurrentCategory";
import { useLoggedOutSuppliersLayoutElementRequestId } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/DataFetching/LoggedOutSuppliersLayoutElement/useLoggedOutSuppliersLayoutElementRequestId";
import { LoggedOutSuppliersPageHeader } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageHeader/LoggedOutSuppliersPageHeader";
import { LoggedOutSuppliersPageTestimonialBanner } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageLayoutElements/__internal__/LoggedOutSuppliersPageTestimonialBanner";
import { trackLoggedOutRetailersSupplierPageBottomCarouselClick } from "@faire/web-api--source/events/loggedOutRetailers/click/supplierPageBottomCarousel";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";
import styled from "styled-components";

export const LoggedOutSuppliersPageLayoutElementRenderer: React.FC<{
  layoutElement: ILayoutElement;
  onVisible: () => void;
}> = ({ layoutElement, onVisible }) => {
  const { isMobile, isXLargeAndAbove } = useViewport();
  const categoryModel = useLoggedOutSuppliersPageCurrentCategory();
  const categoryPathIdentifier = categoryModel?.mostSpecificPathIdentifier;
  const requestId = useLoggedOutSuppliersLayoutElementRequestId();

  switch (layoutElement.type) {
    case ILayoutElement.Type.TESTIMONIAL_CAROUSEL:
      return <LoggedOutSuppliersPageTestimonialBanner />;
    case ILayoutElement.Type.MOST_POPULAR_BRANDS:
      return (
        <Column className="px-4 pt-2 pb-6 lg:px-12">
          <StyledLayoutElementBasedCarousel
            layoutElement={layoutElement}
            requestId={requestId}
            arrowOffsetX={-4}
            itemGap={isXLargeAndAbove ? 32 : undefined}
          />
        </Column>
      );
    case ILayoutElement.Type.AVATAR_CAROUSEL: {
      const tilesLength = layoutElement.layout_element_data?.length ?? 0;
      const itemsPerSlide = {
        mobileAndAbove: Math.min(3, tilesLength),
        tabletAndAbove: Math.min(6, tilesLength),
        desktopAndAbove: Math.min(8, tilesLength),
      };

      return (
        <StyledCarouselWrapper
          style={{ margin: isMobile ? "0" : `${getSpacing("0", "5x")}` }}
        >
          <LayoutElementBasedCarousel
            peekNext={isMobile}
            peekNextPercent={{
              mobileAndAbove: 25,
              tabletAndAbove: 0,
            }}
            layoutElement={layoutElement}
            requestId={requestId}
            itemsPerSlide={itemsPerSlide}
            onItemClick={() => {
              trackLoggedOutRetailersSupplierPageBottomCarouselClick(
                `C${categoryPathIdentifier}`
              );
            }}
            onVisible={onVisible}
          />
          <Spacer height={{ mobileAndAbove: "0", tabletAndAbove: "7x" }} />
        </StyledCarouselWrapper>
      );
    }
    case ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID: {
      return <LoggedOutSuppliersPageHeader title={layoutElement.main_text} />;
    }
    default:
      return null;
  }
};

const StyledCarouselWrapper = styled.div`
  ${responsiveMediaQueries.mobile} {
    ${TitleStyles.Container} {
      padding-left: 20px;
    }
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    ${TitleStyles.Container} {
      align-items: center;
    }
  }
`;

const StyledLayoutElementBasedCarousel = styled(LayoutElementBasedCarousel)`
  ${TitleStyles.MainTitle} {
    ${TypographyStyles({
      variant: "subheadingSansMedium",
    })}
  }
`;
